import { Tooltip } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { SearchIcon } from '@blocksuite/icons/rc';
import clsx from 'clsx';
import type { HTMLAttributes, KeyboardEventHandler, Ref } from 'react';

import { AFFINE_QUICK_SEARCH_SHORTCUT } from '../../../../commands/affine-keybindings';
import * as styles from './index.css';

interface QuickSearchInputProps extends HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
  showTooltip?: boolean;
}

// Although it is called an input, it is actually a button.
export function QuickSearchInput({
  onClick,
  onKeyDown,
  showTooltip = true,
  ...props
}: QuickSearchInputProps) {
  const t = useI18n();
  const label = t['Quick search']();
  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = event => {
    onKeyDown?.(event);
    if (
      event.defaultPrevented ||
      (event.key !== 'Enter' && event.key !== ' ')
    ) {
      return;
    }
    event.preventDefault();
    event.currentTarget.click();
  };

  return (
    <Tooltip
      content={showTooltip ? label : undefined}
      shortcut={showTooltip ? AFFINE_QUICK_SEARCH_SHORTCUT : undefined}
      side="right"
    >
      <div
        {...props}
        aria-label={label}
        className={clsx([props.className, styles.root])}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        <SearchIcon className={styles.icon} />
        <span className={styles.quickSearchBarEllipsisStyle}>{label}</span>
      </div>
    </Tooltip>
  );
}
